import React, { useCallback, useEffect, useRef, useState } from "react";

const PREVIEW_COUNT = 6;

/**
 * "Live from the show" concert gallery.
 * Accepts an array of photo URLs (or {image_url}/{image_watermarked_url} objects).
 * Renders nothing when there are no photos linked.
 * Shows up to 6 photos with an expand control; lightbox supports next/prev/swipe.
 *
 * @param {(string|{image_url?:string,image_watermarked_url?:string})[]} [photos=[]]
 * @param {boolean} [compact=false]  Smaller left-column layout (desktop event page)
 * @param {string} [className=""]
 */
export default function EventGallery({ photos = [], compact = false, className = "" }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const touchStartX = useRef(null);

  const urls = (photos || [])
    .map((p) => (typeof p === "string" ? p : p?.image_watermarked_url || p?.image_url))
    .filter(Boolean);

  const hasMore = urls.length > PREVIEW_COUNT;
  const visibleUrls = expanded || !hasMore ? urls : urls.slice(0, PREVIEW_COUNT);

  const close = useCallback(() => setActiveIndex(null), []);

  const goNext = useCallback(() => {
    setActiveIndex((i) => {
      if (i === null || urls.length === 0) return i;
      return (i + 1) % urls.length;
    });
  }, [urls.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => {
      if (i === null || urls.length === 0) return i;
      return (i - 1 + urls.length) % urls.length;
    });
  }, [urls.length]);

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIndex, close, goNext, goPrev]);

  if (urls.length === 0) return null;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) goNext();
    else goPrev();
  };

  return (
    <section
      className={
        compact
          ? `w-full max-w-xl py-8 md:py-10 ${className}`
          : `mx-auto w-full max-w-7xl px-4 py-12 sm:px-8 md:px-10 md:py-16 lg:px-[40px] ${className}`
      }
    >
      <p className="mb-1.5 text-left text-[11px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
        Live from the show
      </p>
      <h2
        className={`text-left font-bold tracking-tight text-white ${
          compact ? "mb-5 text-xl md:text-2xl" : "mb-8 text-2xl md:text-3xl"
        }`}
      >
        Concert photos
      </h2>

      <div
        className={
          compact
            ? "grid max-w-xl grid-cols-3 gap-2 sm:gap-2.5"
            : "grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4"
        }
      >
        {visibleUrls.map((url, i) => (
          <button
            key={`${url}-${i}`}
            type="button"
            onClick={() => setActiveIndex(i)}
            className={`group relative overflow-hidden rounded-md ring-[0.5px] ring-white/10 transition hover:ring-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright ${
              compact ? "aspect-square" : "aspect-[4/3] rounded-lg"
            }`}
          >
            <img
              src={url}
              alt={`Concert photo ${i + 1}`}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
            />
          </button>
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 text-left text-sm font-semibold text-brand-blue-bright transition hover:text-white"
        >
          {expanded
            ? "Show fewer photos"
            : "View more"}
        </button>
      )}

      {/* Lightbox with next / prev / swipe */}
      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/90 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Concert photo viewer"
          onClick={close}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-label="Close"
            className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>

          {urls.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:left-6"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          <div
            className="relative flex max-h-[90vh] max-w-[min(92vw,960px)] flex-col items-center"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button
              type="button"
              onClick={goNext}
              className="block max-h-[82vh] w-full cursor-pointer border-0 bg-transparent p-0"
              aria-label="Next photo"
            >
              <img
                src={urls[activeIndex]}
                alt={`Concert photo ${activeIndex + 1} of ${urls.length}`}
                className="mx-auto max-h-[82vh] max-w-full rounded-lg object-contain"
                draggable={false}
              />
            </button>
            <p className="mt-3 text-sm text-white/55">
              {activeIndex + 1} / {urls.length}
              {urls.length > 1 ? " · tap or swipe for next" : ""}
            </p>
          </div>

          {urls.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              aria-label="Next photo"
              className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-6"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      )}
    </section>
  );
}
